import {animate, state, style, transition, trigger} from '@angular/animations';
import {Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {ButtonColor, ButtonIconPosition, ButtonSize, ButtonType} from '@npck/libs/ui-kit';
import {StatusStyle} from '../../status/status.component';

export const ELLIPSIS_SMALL_TEXT_MAX_LENGTH = 12;
export const ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH = 24;
export const ELLIPSIS_LARGE_TEXT_MAX_LENGTH = 48;

export function applySmallEllipsis(text: string): Partial<Column> {
    return text?.length > ELLIPSIS_SMALL_TEXT_MAX_LENGTH
        ? {cellStyle: {ellipsisText: true}, tooltip: {text}}
        : {};
}

export function applyMediumEllipsis(text: string): Partial<Column> {
    return text?.length > ELLIPSIS_MEDIUM_TEXT_MAX_LENGTH
        ? {cellStyle: {ellipsisTextMedium: true}, tooltip: {text}}
        : {};
}

export function applyLargeEllipsis(text: string): Partial<Column> {
    return text?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH
        ? {cellStyle: {ellipsisTextLarge: true}, tooltip: {text}}
        : {};
}

@Component({
    selector: 'af-table',
    templateUrl: 'table.component.html',
    styleUrls: ['table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({height: '0px', minHeight: '0'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false,
})
export class TableComponent implements OnChanges {
    @ViewChildren('cells') cell: QueryList<ElementRef>;
    @Input() expandableColumnName: string;
    @Input() totalElementsTitle: string;
    @Input() amountTitle: string;
    @Input() enableScroll: boolean = true;
    @Input() enableSelection: boolean = false;
    @Input() addTranslationPrefix: boolean = true;
    @Input() enableStickyHeader: boolean = true;
    @Input() dataSource: Row[];
    @Input() height: 'default' | 'big' | 'medium' = `default`;
    @Input() withoutBorder = false;
    @Input() withHeaderBackground = false;
    @Input() style: 'default' | 'warning' | 'error' = 'default';
    @Input() sortedBy: SortEvent;
    @Input() columnTranslationPrefixes: Record<string, string> = {};
    @Output() sortByListener = new EventEmitter<SortEvent>();
    displayedColumns: string[];
    ColumnType = ColumnType;
    displayedColumnsWithExpandable: string[];
    expandedElement?: Row;
    sortableColumns: string[];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.dataSource || changes.expandableColumnName) {
            this.recomputeColumns();
        }
    }

    scrollTable(shouldScroll: boolean): void {
        if (shouldScroll) {
            this.cell.first.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }

    handleRowClick(row: Row): void {
        row?.click?.();
        if (this.expandableColumnName) {
            this.expandedElement = this.expandedElement === row ? null : row;
        }
    }

    sortTable(column: string): void {
        this.sortByListener.emit({
            columnName: column,
            direction: this.sortedBy?.direction === 'desc' ? 'asc' : 'desc',
        });
    }

    private recomputeColumns(): void {
        if (!this.dataSource) {
            return;
        }
        this.displayedColumns = Array.from(
            new Set(this.dataSource
                .map(row => Object.keys(row))
                .reduce((acc, val) => acc.concat(val), []),
            ),
        )
            .filter(columnName => (columnName as keyof Row) !== 'rowStyle')
            .filter(columnName => (columnName as keyof Row) !== 'click')
            .filter(columnName => (columnName as keyof Row) !== 'isSelected');
        this.sortableColumns = this.displayedColumns.filter(column => (this.dataSource[0]?.[column] as Column)?.sortable);
        this.displayedColumnsWithExpandable = this.expandableColumnName && ['expand', ...this.displayedColumns] || this.displayedColumns;
    }
}

export interface SortEvent {
    columnName: string;
    direction: SortDirection;
}

export declare type SortDirection = 'asc' | 'desc';

export interface Column {
    text: string;
    type: ColumnType;
    cellStyle?: CellStyle;
    tooltip?: Tooltip;
    sortable?: boolean;
    click?: () => void;
}

export interface MaskedText extends Column {
    mask?: string;
    prefix?: string;
    specialCharacters?: string[];
}

export interface Checkbox extends Column {
    change: (checked: boolean) => void;
    isChecked: boolean;
    isDisabled: boolean;
}

export interface WithLogoColumn extends Column {
    logoUrl: string;
}

export interface ExpandedColumn extends Column {
    data: any[];
}

export interface ButtonColumn extends Column {
    icon: string;
    buttonType: ButtonType;
    buttonSize: ButtonSize;
    color: ButtonColor;
    iconPosition: ButtonIconPosition;
    click: () => void;
    additionalIcons?: IconClickable[];
    spinnerId: string;
}

export interface CustomLayoutColumn extends Column {
    layoutContentItems: CustomLayoutItem[];
}

export interface StatusColumn extends Column {
    style: StatusStyle;
}

export interface CustomLayoutItem {
    type: 'text' | 'icon'
    text: string;
    iconSize?: string;
    iconColor?: string;
    click?: () => void;
    disabled?: boolean;
    tooltip?: string;
}

export interface CellStyle {
    ellipsisText?: boolean;
    ellipsisTextMedium?: boolean;
    ellipsisTextLarge?: boolean;
    layoutDirection?: 'row' | 'column';
    layoutAlignFirst?: 'start' | 'center' | 'end';
    layoutAlignSecond?: 'start' | 'center';
    layoutGap?: string;
}

export interface Tooltip {
    text: string;
}

export interface IconClickable {
    name: string;
    click: () => void;
}

export interface Row {
    [key: string]: Column | RowStyle | (() => void) | boolean;

    isSelected?: boolean;
    click?: () => void;
}

export declare type RowStyle = 'warning' | 'error' | 'error-light' | 'disabled';

export enum ColumnType {
    TEXT = 'TEXT',
    LINK = 'LINK',
    MASKED_TEXT = 'MASKED_TEXT',
    PRIORITY = 'PRIORITY',
    HIDDEN = 'HIDDEN',
    LOGO = 'LOGO',
    MONEY = 'MONEY',
    DIGITS = 'DIGITS',
    STATUS = 'STATUS',
    CUSTOM_LAYOUT = 'CUSTOM_LAYOUT',
    STATUS_WITH_ICON_BUTTON = 'STATUS_WITH_ICON_BUTTON',
    PROGRESS = 'PROGRESS',
    BUTTON = 'BUTTON',
    DATE = 'DATE',
    CHECKBOX = 'CHECKBOX',
    DATETIME = 'DATETIME',
    DATE_TIME_SECONDS = 'DATE_TIME_SECONDS',
    DATE_TIME_MILLIS = 'DATE_TIME_MILLIS',
    TIME_MILLIS = 'TIME_MILLIS',
    EXPANDABLE = 'EXPANDABLE',
}
